/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  axios,
  AxiosResponsePromise,
  AxiosRequestExtraConfig,
  AxiosRequestConfig,
} from '@seiue/axios'
import {
  createConfigBuilder,
  useQueryApi,
  useMutationApi,
  UseQueryApiReturns,
} from '../utils'
import { MutationOptions, QueryOptions, Expand } from '../types'
import { PaperBlock } from './paper-block'
import { PaperBlockReq } from './paper-block-req'
import { SortBlock } from './sort-block'
import { PaperReq } from './paper-req'
import { Paper } from './paper'
import { PaperAnswer } from './paper-answer'
import { PaperAnswerReq } from './paper-answer-req'
import { PaperAnswerLog } from './paper-answer-log'
import { BizTypeEnum } from './biz-type-enum'
import { PaperReviewReq } from './paper-review-req'
import { PaperReview } from './paper-review'
import { BatchUpdateWithIdReq } from './batch-update-with-id-req'
import { PaperReviewLog } from './paper-review-log'
import { QueryBlocksQuery } from './query-blocks-query'
import { LoadBlockQuery } from './load-block-query'
import { DeleteBlockQuery } from './delete-block-query'
import { QueryPapersQuery } from './query-papers-query'
import { LoadPaperQuery } from './load-paper-query'
import { LoadPaperQuestionsQuery } from './load-paper-questions-query'
import { QueryAnswersQuery } from './query-answers-query'
import { QueryPublishedAnswersQuery } from './query-published-answers-query'
import { QueryReviewsQuery } from './query-reviews-query'
import { QueryPublishedReviewsQuery } from './query-published-reviews-query'

const buildConfig = createConfigBuilder('growp')

export const paperApi = {
  queryBlocksConfig(
    paperId: number,
    queryBlocksQuery?: QueryBlocksQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/paper/blocks',
      {
        paperId,
      },
      queryBlocksQuery
    )
  },

  /**
   * @summary 【前/后台】查询试卷块列表
   * @param paperId
   * @param [queryBlocksQuery]
   * @param [queryBlocksQuery.expand]
   * @param [queryBlocksQuery.paginated] 是否分页，默认 1
   * @param [queryBlocksQuery.page] 显示的数据页，默认 1
   * @param [queryBlocksQuery.perPage] 每页显示数量，默认 20
   * @param [queryBlocksQuery.sort]
   * @param [options]
   * @return AxiosResponsePromise<PaperBlock[]>
   */
  queryBlocks<TQuery extends QueryBlocksQuery>(
    paperId: number,
    queryBlocksQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<PaperBlock, TQuery['expand']>[]> {
    return axios(
      paperApi.queryBlocksConfig(paperId, queryBlocksQuery, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? _data.map((d: any) => new PaperBlock(d)) : _data
      return { data, ...rest }
    }) as any
  },

  useQueryBlocks<TQuery extends QueryBlocksQuery>(
    {
      paperId,
      query,
    }: {
      paperId: number
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<PaperBlock, TQuery['expand']>[]> {
    return useQueryApi<Expand<PaperBlock, TQuery['expand']>[]>(
      paperApi.queryBlocks,
      'Paper.queryBlocks',
      queryOptions,
      paperId,
      query
    )
  },

  createBlockConfig(
    paperBlockReq: PaperBlockReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/paper/blocks',
      {},
      null,
      paperBlockReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          questions: (v: any) => v || [],
          parentId: (v: any) => v || 0,
          hash: (v: any) => v || '',
          questionId: (v: any) => v || 0,
          questionIds: (v: any) => v || [],
          content: (v: any) => v || '',
          attachments: (v: any) => v || [],
          weight: (v: any) => v || 0,
        },
      }
    )
  },

  /**
   * @summary 【后台】创建试卷块
   * @param paperBlockReq
   * @param [options]
   * @return AxiosResponsePromise<PaperBlock>
   */
  createBlock(
    paperBlockReq: PaperBlockReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<PaperBlock> {
    return axios(paperApi.createBlockConfig(paperBlockReq, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new PaperBlock(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useCreateBlock(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: PaperBlockReq) => paperApi.createBlock(payload),
      mutationOptions
    )
  },

  batchCreateBlockConfig(
    paperBlockReq: PaperBlockReq[],
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/paper/blocks/$batch',
      {},
      null,
      paperBlockReq,
      {
        format: 'json',
        isArray: true,
        defaults: {
          questions: (v: any) => v || [],
          parentId: (v: any) => v || 0,
          hash: (v: any) => v || '',
          questionId: (v: any) => v || 0,
          questionIds: (v: any) => v || [],
          content: (v: any) => v || '',
          attachments: (v: any) => v || [],
          weight: (v: any) => v || 0,
        },
      }
    )
  },

  /**
   * @summary 【后台】批量创建试卷块
   * @param paperBlockReq
   * @param [options]
   * @return AxiosResponsePromise<PaperBlock[]>
   */
  batchCreateBlock(
    paperBlockReq: PaperBlockReq[],
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<PaperBlock[]> {
    return axios(paperApi.batchCreateBlockConfig(paperBlockReq, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? _data.map((d: any) => new PaperBlock(d)) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useBatchCreateBlock(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: PaperBlockReq[]) => paperApi.batchCreateBlock(payload),
      mutationOptions
    )
  },

  batchSortBlocksConfig(
    paperId: number,
    sortBlock: SortBlock[],
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/paper/blocks/$batch/sort',
      {
        paperId,
      },
      null,
      sortBlock,
      {
        format: 'json',
        isArray: true,
        defaults: {
          parentId: (v: any) => v || 0,
          weight: (v: any) => v || 0,
        },
      }
    )
  },

  /**
   * @summary 【后台】批量排序试卷块
   * @param paperId
   * @param sortBlock
   * @param [options]
   * @return AxiosResponsePromise<number>
   */
  batchSortBlocks(
    paperId: number,
    sortBlock: SortBlock[],
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<number> {
    return axios(
      paperApi.batchSortBlocksConfig(paperId, sortBlock, options)
    ) as any
  },

  useBatchSortBlocks(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ paperId, payload }: { paperId: number; payload: SortBlock[] }) =>
        paperApi.batchSortBlocks(paperId, payload),
      mutationOptions
    )
  },

  updateBlockConfig(
    id: number,
    paperBlockReq: PaperBlockReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/paper/blocks/{id}',
      {
        id,
      },
      null,
      paperBlockReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          questions: (v: any) => v || [],
          parentId: (v: any) => v || 0,
          hash: (v: any) => v || '',
          questionId: (v: any) => v || 0,
          questionIds: (v: any) => v || [],
          content: (v: any) => v || '',
          attachments: (v: any) => v || [],
          weight: (v: any) => v || 0,
        },
      }
    )
  },

  /**
   * @summary 【后台】修改试卷块
   * @param id
   * @param paperBlockReq
   * @param [options]
   * @return AxiosResponsePromise<PaperBlock>
   */
  updateBlock(
    id: number,
    paperBlockReq: PaperBlockReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<PaperBlock> {
    return axios(paperApi.updateBlockConfig(id, paperBlockReq, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new PaperBlock(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useUpdateBlock(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ id, payload }: { id: number; payload: PaperBlockReq }) =>
        paperApi.updateBlock(id, payload),
      mutationOptions
    )
  },

  loadBlockConfig(
    id: number,
    loadBlockQuery?: LoadBlockQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/paper/blocks/{id}',
      {
        id,
      },
      loadBlockQuery
    )
  },

  /**
   * @summary 【前/后台】查询试卷块
   * @param id
   * @param [loadBlockQuery]
   * @param [loadBlockQuery.expand]
   * @param [options]
   * @return AxiosResponsePromise<PaperBlock[]>
   */
  loadBlock<TQuery extends LoadBlockQuery>(
    id: number,
    loadBlockQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<PaperBlock, TQuery['expand']>[]> {
    return axios(paperApi.loadBlockConfig(id, loadBlockQuery, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? _data.map((d: any) => new PaperBlock(d)) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useLoadBlock<TQuery extends LoadBlockQuery>(
    {
      id,
      query,
    }: {
      id: number
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<PaperBlock, TQuery['expand']>[]> {
    return useQueryApi<Expand<PaperBlock, TQuery['expand']>[]>(
      paperApi.loadBlock,
      'Paper.loadBlock',
      queryOptions,
      id,
      query
    )
  },

  deleteBlockConfig(
    paperId: number,
    id: number,
    deleteBlockQuery?: DeleteBlockQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'delete',
      '/paper/blocks/{id}',
      {
        paperId,
        id,
      },
      deleteBlockQuery
    )
  },

  /**
   * @summary 【后台】删除试卷块
   * @param paperId
   * @param id
   * @param [deleteBlockQuery]
   * @param [deleteBlockQuery.onlyDeleteSelf] 是否仅删除自己（默认否，删除自身同时删除下含所有的子、孙块；否则仅删除本身）
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  deleteBlock(
    paperId: number,
    id: number,
    deleteBlockQuery?: DeleteBlockQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(
      paperApi.deleteBlockConfig(paperId, id, deleteBlockQuery, options)
    ) as any
  },

  useDeleteBlock(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({
        paperId,
        id,
        query,
      }: {
        paperId: number
        id: number
        query?: DeleteBlockQuery
      }) => paperApi.deleteBlock(paperId, id, query),
      mutationOptions
    )
  },

  copyBlockConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'post', '/paper/blocks/{id}/copy', {
      id,
    })
  },

  /**
   * @summary 【后台】复制试卷块
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<PaperBlock[]>
   */
  copyBlock(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<PaperBlock[]> {
    return axios(paperApi.copyBlockConfig(id, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? _data.map((d: any) => new PaperBlock(d)) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useCopyBlock(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (id: number) => paperApi.copyBlock(id),
      mutationOptions
    )
  },

  createPaperConfig(
    paperReq: PaperReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'post', '/paper/papers', {}, null, paperReq, {
      format: 'json',
      isArray: false,
      defaults: {
        id: (v: any) => v || 0,
        name: (v: any) => v || '',
        description: (v: any) => v || '',
        blockCount: (v: any) => v || 0,
        scoreSum: (v: any) => v || '',
        originId: (v: any) => v || 0,
        isShow: (v: any) => v || 0,
        showScore: (v: any) => v || 0,
      },
    })
  },

  /**
   * @summary 【后台】创建试卷
   * @param paperReq
   * @param [options]
   * @return AxiosResponsePromise<Paper>
   */
  createPaper(
    paperReq: PaperReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Paper> {
    return axios(paperApi.createPaperConfig(paperReq, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new Paper(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useCreatePaper(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: PaperReq) => paperApi.createPaper(payload),
      mutationOptions
    )
  },

  queryPapersConfig(
    queryPapersQuery?: QueryPapersQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'get', '/paper/papers', {}, queryPapersQuery)
  },

  /**
   * @summary 【后台】查询试卷列表
   * @param [queryPapersQuery]
   * @param [queryPapersQuery.nameLike]
   * @param [queryPapersQuery.noCopy] 不显示副本试卷
   *  - 默认 是
   * @param [queryPapersQuery.idIn]
   * @param [queryPapersQuery.originIdIn] 根据拷贝源 ID 筛选
   * @param [queryPapersQuery.isRand] 是否抽题模式
   * @param [queryPapersQuery.expand]
   * @param [queryPapersQuery.paginated] 是否分页，默认 1
   * @param [queryPapersQuery.page] 显示的数据页，默认 1
   * @param [queryPapersQuery.perPage] 每页显示数量，默认 20
   * @param [queryPapersQuery.sort]
   * @param [options]
   * @return AxiosResponsePromise<Paper[]>
   */
  queryPapers<TQuery extends QueryPapersQuery>(
    queryPapersQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<Paper, TQuery['expand']>[]> {
    return axios(paperApi.queryPapersConfig(queryPapersQuery, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? _data.map((d: any) => new Paper(d)) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useQueryPapers<TQuery extends QueryPapersQuery>(
    query?: TQuery,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<Paper, TQuery['expand']>[]> {
    return useQueryApi<Expand<Paper, TQuery['expand']>[]>(
      paperApi.queryPapers,
      'Paper.queryPapers',
      queryOptions,
      query
    )
  },

  updatePaperConfig(
    id: number,
    paperReq: PaperReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/paper/papers/{id}',
      {
        id,
      },
      null,
      paperReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          id: (v: any) => v || 0,
          name: (v: any) => v || '',
          description: (v: any) => v || '',
          blockCount: (v: any) => v || 0,
          scoreSum: (v: any) => v || '',
          originId: (v: any) => v || 0,
          isShow: (v: any) => v || 0,
          showScore: (v: any) => v || 0,
        },
      }
    )
  },

  /**
   * @summary 【后台】修改试卷
   * @param id
   * @param paperReq
   * @param [options]
   * @return AxiosResponsePromise<Paper>
   */
  updatePaper(
    id: number,
    paperReq: PaperReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Paper> {
    return axios(paperApi.updatePaperConfig(id, paperReq, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new Paper(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useUpdatePaper(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ id, payload }: { id: number; payload: PaperReq }) =>
        paperApi.updatePaper(id, payload),
      mutationOptions
    )
  },

  deletePaperConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'delete', '/paper/papers/{id}', {
      id,
    })
  },

  /**
   * @summary 【后台】删除试卷
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  deletePaper(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(paperApi.deletePaperConfig(id, options)) as any
  },

  useDeletePaper(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (id: number) => paperApi.deletePaper(id),
      mutationOptions
    )
  },

  loadPaperConfig(
    id: number,
    loadPaperQuery?: LoadPaperQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/paper/papers/{id}',
      {
        id,
      },
      loadPaperQuery
    )
  },

  /**
   * @summary 【前/后台】查询单个试卷
   * @param id
   * @param [loadPaperQuery]
   * @param [loadPaperQuery.expand]
   * @param [options]
   * @return AxiosResponsePromise<Paper>
   */
  loadPaper<TQuery extends LoadPaperQuery>(
    id: number,
    loadPaperQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<Paper, TQuery['expand']>> {
    return axios(paperApi.loadPaperConfig(id, loadPaperQuery, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new Paper(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useLoadPaper<TQuery extends LoadPaperQuery>(
    {
      id,
      query,
    }: {
      id: number
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<Paper, TQuery['expand']>> {
    return useQueryApi<Expand<Paper, TQuery['expand']>>(
      paperApi.loadPaper,
      'Paper.loadPaper',
      queryOptions,
      id,
      query
    )
  },

  copyPaperConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'post', '/paper/papers/{id}/copy', {
      id,
    })
  },

  /**
   * @summary 【后台】复制试卷
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<Paper>
   */
  copyPaper(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Paper> {
    return axios(paperApi.copyPaperConfig(id, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new Paper(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useCopyPaper(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (id: number) => paperApi.copyPaper(id),
      mutationOptions
    )
  },

  loadPaperQuestionsConfig(
    id: number,
    loadPaperQuestionsQuery?: LoadPaperQuestionsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/paper/papers/{id}/questions',
      {
        id,
      },
      loadPaperQuestionsQuery
    )
  },

  /**
   * @summary 【后台】测验-查询试卷内容，可以只返回单选多选题
   * @param id
   * @param [loadPaperQuestionsQuery]
   * @param [loadPaperQuestionsQuery.isRand]
   * @param [options]
   * @return AxiosResponsePromise<PaperBlockReq[]>
   */
  loadPaperQuestions(
    id: number,
    loadPaperQuestionsQuery?: LoadPaperQuestionsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<PaperBlockReq[]> {
    return axios(
      paperApi.loadPaperQuestionsConfig(id, loadPaperQuestionsQuery, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? _data.map((d: any) => new PaperBlockReq(d)) : _data
      return { data, ...rest }
    }) as any
  },

  useLoadPaperQuestions(
    {
      id,
      query,
    }: {
      id: number
      query?: LoadPaperQuestionsQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<PaperBlockReq[]> {
    return useQueryApi<PaperBlockReq[]>(
      paperApi.loadPaperQuestions,
      'Paper.loadPaperQuestions',
      queryOptions,
      id,
      query
    )
  },

  queryAnswersConfig(
    paperId: number,
    queryAnswersQuery?: QueryAnswersQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/paper/{paperId}/answers',
      {
        paperId,
      },
      queryAnswersQuery
    )
  },

  /**
   * @summary 【前台】答题人获取答案列表
   * @param paperId
   * @param [queryAnswersQuery]
   * @param [queryAnswersQuery.questionIdIn] 根据题目筛选
   * @param [queryAnswersQuery.bizId] 根据关联业务 id 筛选
   * @param [queryAnswersQuery.bizType] 根据关联业务类型筛选
   * @param [queryAnswersQuery.isPublished]
   * @param [queryAnswersQuery.expand]
   * @param [queryAnswersQuery.paginated] 是否分页，默认 1
   * @param [queryAnswersQuery.page] 显示的数据页，默认 1
   * @param [queryAnswersQuery.perPage] 每页显示数量，默认 20
   * @param [queryAnswersQuery.sort]
   * @param [options]
   * @return AxiosResponsePromise<PaperAnswer[]>
   */
  queryAnswers<TQuery extends QueryAnswersQuery>(
    paperId: number,
    queryAnswersQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<PaperAnswer, TQuery['expand']>[]> {
    return axios(
      paperApi.queryAnswersConfig(paperId, queryAnswersQuery, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? _data.map((d: any) => new PaperAnswer(d)) : _data
      return { data, ...rest }
    }) as any
  },

  useQueryAnswers<TQuery extends QueryAnswersQuery>(
    {
      paperId,
      query,
    }: {
      paperId: number
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<PaperAnswer, TQuery['expand']>[]> {
    return useQueryApi<Expand<PaperAnswer, TQuery['expand']>[]>(
      paperApi.queryAnswers,
      'Paper.queryAnswers',
      queryOptions,
      paperId,
      query
    )
  },

  batchSubmitAnswersConfig(
    paperId: string,
    paperAnswerReq: PaperAnswerReq[],
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/paper/{paperId}/answers/$batch',
      {
        paperId,
      },
      null,
      paperAnswerReq,
      {
        format: 'json',
        isArray: true,
        defaults: {
          value: (v: any) => v || [],
          isPublished: (v: any) => v || false,
          bizId: (v: any) => v || 0,
          bizType: (v: any) => v || null,
        },
      }
    )
  },

  /**
   * @summary 【前台】批量提交试卷答案
   * @param paperId
   * @param paperAnswerReq
   * @param [options]
   * @return AxiosResponsePromise<PaperAnswer[]>
   */
  batchSubmitAnswers(
    paperId: string,
    paperAnswerReq: PaperAnswerReq[],
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<PaperAnswer[]> {
    return axios(
      paperApi.batchSubmitAnswersConfig(paperId, paperAnswerReq, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? _data.map((d: any) => new PaperAnswer(d)) : _data
      return { data, ...rest }
    }) as any
  },

  useBatchSubmitAnswers(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ paperId, payload }: { paperId: string; payload: PaperAnswerReq[] }) =>
        paperApi.batchSubmitAnswers(paperId, payload),
      mutationOptions
    )
  },

  queryPublishedAnswersConfig(
    paperId: number,
    queryPublishedAnswersQuery?: QueryPublishedAnswersQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/paper/{paperId}/answers/published',
      {
        paperId,
      },
      queryPublishedAnswersQuery
    )
  },

  /**
   * @summary 【前台】批改人获取答案列表[获取最新已发布答案]
   * @param paperId
   * @param [queryPublishedAnswersQuery]
   * @param [queryPublishedAnswersQuery.bizId] 根据关联业务 id 筛选
   * @param [queryPublishedAnswersQuery.bizType] 根据关联业务类型筛选
   * @param [queryPublishedAnswersQuery.submitterId]
   * @param [queryPublishedAnswersQuery.submitterDomain]
   * @param [queryPublishedAnswersQuery.questionIdIn] 根据题目筛选
   * @param [queryPublishedAnswersQuery.expand]
   * @param [options]
   * @return AxiosResponsePromise<PaperAnswerLog[]>
   */
  queryPublishedAnswers<TQuery extends QueryPublishedAnswersQuery>(
    paperId: number,
    queryPublishedAnswersQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<PaperAnswerLog, TQuery['expand']>[]> {
    return axios(
      paperApi.queryPublishedAnswersConfig(
        paperId,
        queryPublishedAnswersQuery,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? _data.map((d: any) => new PaperAnswerLog(d)) : _data
      return { data, ...rest }
    }) as any
  },

  useQueryPublishedAnswers<TQuery extends QueryPublishedAnswersQuery>(
    {
      paperId,
      query,
    }: {
      paperId: number
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<PaperAnswerLog, TQuery['expand']>[]> {
    return useQueryApi<Expand<PaperAnswerLog, TQuery['expand']>[]>(
      paperApi.queryPublishedAnswers,
      'Paper.queryPublishedAnswers',
      queryOptions,
      paperId,
      query
    )
  },

  createReviewConfig(
    paperId: number,
    bizId: number,
    bizType: BizTypeEnum,
    activityTaskId: number,
    paperReviewReq: PaperReviewReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/paper/{paperId}/reviews',
      {
        paperId,
        bizId,
        bizType,
        activityTaskId,
      },
      null,
      paperReviewReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          id: (v: any) => v || 0,
          submitterId: (v: any) => v || 0,
          result: (v: any) => v || null,
          gainedScore: (v: any) => v || null,
          content: (v: any) => v || '',
          attachments: (v: any) => v || [],
          isPublished: (v: any) => v || false,
        },
      }
    )
  },

  /**
   * @summary 【后台】创建批阅
   * @param paperId
   * @param bizId
   * @param bizType
   * @param activityTaskId 关联的活动任务 ID
   * @param paperReviewReq
   * @param [options]
   * @return AxiosResponsePromise<PaperReview>
   */
  createReview(
    paperId: number,
    bizId: number,
    bizType: BizTypeEnum,
    activityTaskId: number,
    paperReviewReq: PaperReviewReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<PaperReview> {
    return axios(
      paperApi.createReviewConfig(
        paperId,
        bizId,
        bizType,
        activityTaskId,
        paperReviewReq,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new PaperReview(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useCreateReview(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({
        paperId,
        bizId,
        bizType,
        activityTaskId,
        payload,
      }: {
        paperId: number
        bizId: number
        bizType: BizTypeEnum
        activityTaskId: number
        payload: PaperReviewReq
      }) =>
        paperApi.createReview(paperId, bizId, bizType, activityTaskId, payload),
      mutationOptions
    )
  },

  queryReviewsConfig(
    paperId: number,
    queryReviewsQuery?: QueryReviewsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/paper/{paperId}/reviews',
      {
        paperId,
      },
      queryReviewsQuery
    )
  },

  /**
   * @summary 【前台】批阅人获取批阅列表
   * @param paperId
   * @param [queryReviewsQuery]
   * @param [queryReviewsQuery.questionIdIn] 根据题目筛选
   * @param [queryReviewsQuery.answerIdIn]
   * @param [queryReviewsQuery.reviewerIdIn]
   * @param [queryReviewsQuery.submitterIdIn]
   * @param [queryReviewsQuery.submitterDomain]
   * @param [queryReviewsQuery.isPublished]
   * @param [queryReviewsQuery.resultIn]
   * @param [queryReviewsQuery.expand]
   * @param [queryReviewsQuery.paginated] 是否分页，默认 1
   * @param [queryReviewsQuery.page] 显示的数据页，默认 1
   * @param [queryReviewsQuery.perPage] 每页显示数量，默认 20
   * @param [queryReviewsQuery.sort]
   * @param [options]
   * @return AxiosResponsePromise<PaperReview[]>
   */
  queryReviews<TQuery extends QueryReviewsQuery>(
    paperId: number,
    queryReviewsQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<PaperReview, TQuery['expand']>[]> {
    return axios(
      paperApi.queryReviewsConfig(paperId, queryReviewsQuery, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? _data.map((d: any) => new PaperReview(d)) : _data
      return { data, ...rest }
    }) as any
  },

  useQueryReviews<TQuery extends QueryReviewsQuery>(
    {
      paperId,
      query,
    }: {
      paperId: number
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<PaperReview, TQuery['expand']>[]> {
    return useQueryApi<Expand<PaperReview, TQuery['expand']>[]>(
      paperApi.queryReviews,
      'Paper.queryReviews',
      queryOptions,
      paperId,
      query
    )
  },

  publishReviewsConfig(
    paperId: string,
    batchUpdateWithIdReq: BatchUpdateWithIdReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/paper/{paperId}/reviews/$batch/published',
      {
        paperId,
      },
      null,
      batchUpdateWithIdReq,
      {
        format: 'json',
        isArray: false,
        defaults: {},
      }
    )
  },

  /**
   * @summary 【前台】发布批阅
   * @param paperId
   * @param batchUpdateWithIdReq
   * @param [options]
   * @return AxiosResponsePromise<number>
   */
  publishReviews(
    paperId: string,
    batchUpdateWithIdReq: BatchUpdateWithIdReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<number> {
    return axios(
      paperApi.publishReviewsConfig(paperId, batchUpdateWithIdReq, options)
    ) as any
  },

  usePublishReviews(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({
        paperId,
        payload,
      }: {
        paperId: string
        payload: BatchUpdateWithIdReq
      }) => paperApi.publishReviews(paperId, payload),
      mutationOptions
    )
  },

  queryPublishedReviewsConfig(
    paperId: number,
    queryPublishedReviewsQuery?: QueryPublishedReviewsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/paper/{paperId}/reviews/published',
      {
        paperId,
      },
      queryPublishedReviewsQuery
    )
  },

  /**
   * @summary 【前台】答题人获取批阅列表
   * @param paperId
   * @param [queryPublishedReviewsQuery]
   * @param [queryPublishedReviewsQuery.answerIdIn]
   * @param [queryPublishedReviewsQuery.submitterId]
   * @param [queryPublishedReviewsQuery.submitterDomain]
   * @param [queryPublishedReviewsQuery.questionIdIn] 根据题目筛选
   * @param [queryPublishedReviewsQuery.expand]
   * @param [options]
   * @return AxiosResponsePromise<PaperReviewLog[]>
   */
  queryPublishedReviews<TQuery extends QueryPublishedReviewsQuery>(
    paperId: number,
    queryPublishedReviewsQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<PaperReviewLog, TQuery['expand']>[]> {
    return axios(
      paperApi.queryPublishedReviewsConfig(
        paperId,
        queryPublishedReviewsQuery,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? _data.map((d: any) => new PaperReviewLog(d)) : _data
      return { data, ...rest }
    }) as any
  },

  useQueryPublishedReviews<TQuery extends QueryPublishedReviewsQuery>(
    {
      paperId,
      query,
    }: {
      paperId: number
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<PaperReviewLog, TQuery['expand']>[]> {
    return useQueryApi<Expand<PaperReviewLog, TQuery['expand']>[]>(
      paperApi.queryPublishedReviews,
      'Paper.queryPublishedReviews',
      queryOptions,
      paperId,
      query
    )
  },

  updateReviewConfig(
    paperId: string,
    bizId: number,
    bizType: BizTypeEnum,
    activityTaskId: number,
    id: number,
    paperReviewReq: PaperReviewReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/paper/{paperId}/reviews/{id}',
      {
        paperId,
        bizId,
        bizType,
        activityTaskId,
        id,
      },
      null,
      paperReviewReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          id: (v: any) => v || 0,
          submitterId: (v: any) => v || 0,
          result: (v: any) => v || null,
          gainedScore: (v: any) => v || null,
          content: (v: any) => v || '',
          attachments: (v: any) => v || [],
          isPublished: (v: any) => v || false,
        },
      }
    )
  },

  /**
   * @summary 【前台】修改批阅
   * @param paperId
   * @param bizId
   * @param bizType
   * @param activityTaskId 关联的活动任务 ID
   * @param id
   * @param paperReviewReq
   * @param [options]
   * @return AxiosResponsePromise<PaperReview>
   */
  updateReview(
    paperId: string,
    bizId: number,
    bizType: BizTypeEnum,
    activityTaskId: number,
    id: number,
    paperReviewReq: PaperReviewReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<PaperReview> {
    return axios(
      paperApi.updateReviewConfig(
        paperId,
        bizId,
        bizType,
        activityTaskId,
        id,
        paperReviewReq,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new PaperReview(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useUpdateReview(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({
        paperId,
        bizId,
        bizType,
        activityTaskId,
        id,
        payload,
      }: {
        paperId: string
        bizId: number
        bizType: BizTypeEnum
        activityTaskId: number
        id: number
        payload: PaperReviewReq
      }) =>
        paperApi.updateReview(
          paperId,
          bizId,
          bizType,
          activityTaskId,
          id,
          payload
        ),
      mutationOptions
    )
  },
}

// generate uuid for every api function, useful for result-caching & other things
Object.keys(paperApi).forEach((api) => {
  // @ts-ignore
  paperApi[api]._name_ = `Paper.${api}`
})
